// pdf.js page-text extraction. Mirrors the D0.3 fidelity spike's
// "eol" strategy (spikes/pdfjs-fidelity/extract-pdfjs.mjs), which
// proved byte-equal canonical CSV against pdfplumber on demo-bravo.
//
// pdf.js emits text items in reading order with a `hasEOL` flag set
// when the renderer detected a hard line break. Concatenate item.str
// in order; on hasEOL append "\n". This matches what the PDF's own
// text rendering engine considers a visual line break — which is what
// pdfplumber's page.extract_text() reports for text-based PDFs.

import * as pdfjsLib from "pdfjs-dist";
// Vite resolves this to a real URL at build time. The worker is loaded
// lazily by pdf.js when getDocument runs.
// eslint-disable-next-line import/no-unresolved -- vite ?url query
import workerSrc from "pdfjs-dist/build/pdf.worker.mjs?url";

pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

export interface PdfExtractProgress {
  pageNumber: number;
  totalPages: number;
}

/**
 * One visual line with the geometry that identifies its column.
 *
 * Most adapters parse line TEXT and never need this. AppClose does: its
 * record headers wrap unpredictably and a wrapped fragment can itself look
 * like a valid record start, so "is this line a header continuation or a
 * message body?" is answerable only by column. See appclose.ts.
 */
export interface PositionedLine {
  /** Leftmost x of the row — the column identity. */
  x0: number;
  /** Glyph height of the leftmost run (section titles vs date headers). */
  height: number;
  text: string;
}

/** A page's text AND its positioned lines, from a single extraction pass. */
export interface PdfPage {
  text: string;
  lines: PositionedLine[];
}

/**
 * Extract one string per page using the EOL strategy. Returns the array
 * of page texts in document order.
 *
 * `onProgress` is invoked after each page is processed (1-indexed).
 */
export async function extractPdfTextEol(
  data: ArrayBuffer | Uint8Array,
  onProgress?: (p: PdfExtractProgress) => void,
): Promise<string[]> {
  return (await extractPdfPages(data, onProgress)).map((p) => p.text);
}

/**
 * KNOWN LIMIT — pdf.js can drop glyphs pdfplumber recovers.
 *
 * On the real AppClose export, one message's URL loses 17 characters:
 * pdf.js emits "...fbclid=IwZXh0bgNhZW0BMAABHe" and then the next run, with
 * "340tVsiSOGAVr3T8Z" absent from the ENTIRE page's text content — under
 * default options, `disableNormalization`, and `includeMarkedContent` alike.
 * The run it belongs to is 529 pt wide starting at x=71, so it ends at the
 * right margin; the tail appears to be dropped rather than emitted.
 *
 * Measured scope, whole corpus: 1 row of 10,011. Every other cross-engine
 * body difference is either a single space or a rendering artifact the Python
 * side still carries. Recovering it would mean reading glyphs out of
 * getOperatorList instead of getTextContent — a different extractor, for 17
 * characters of a tracking parameter.
 *
 * It does not affect the desktop/CLI path, which uses pdfplumber and is
 * complete. Recorded here so nobody re-derives it from a byte-compare.
 */

/**
 * Extract every page's text AND its positioned lines in ONE pass.
 *
 * `extractPageText` already buckets items into positioned rows on its way to
 * a string, so returning the geometry alongside costs nothing — and the
 * alternative (extracting twice once the format is known) would mean parsing
 * a 124 MB export a second time in the browser. `extractPdfTextEol` is a thin
 * wrapper over this, so existing callers are unchanged.
 */
export async function extractPdfPages(
  data: ArrayBuffer | Uint8Array,
  onProgress?: (p: PdfExtractProgress) => void,
): Promise<PdfPage[]> {
  // Security posture (2026-10-31, assessment F12): these are adversary-
  // authored PDFs by construction. We only ever call getTextContent(); we
  // never build a viewer, annotation layer or scripting manager, so the PDF
  // JavaScript path that CVE-2026-16633 exploited (pdfjs-dist 5.6.83–6.2.107)
  // is never instantiated here. In 6.x `enableScripting` is a viewer-layer
  // option and `isEvalSupported` no longer exists, so there is no getDocument
  // flag to set — keep it that way: if a render path is ever added, it needs
  // `enableScripting: false` on the viewer and a CSP without unsafe-eval.
  const loadingTask = pdfjsLib.getDocument({
    data: data instanceof Uint8Array ? data : new Uint8Array(data),
  });
  const pdf = await loadingTask.promise;
  const pages: PdfPage[] = [];
  try {
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const tc = await page.getTextContent();
      pages.push({ text: extractPageText(tc), lines: extractPageLines(tc) });
      onProgress?.({ pageNumber: i, totalPages: pdf.numPages });
      // Yield a macrotask periodically so the browser can PAINT the page
      // counter. pdf.js resolves getTextContent() from buffered data via
      // microtasks once the worker is warm, and microtasks never yield a
      // paint — so without this the per-page counter is committed by React
      // but coalesced into a single paint at the end, jumping 0→done
      // invisibly on large/warm extractions (observed on a 1,215-page export
      // late in a multi-file session). setTimeout(0) is a macrotask, which
      // gives the event loop a rendering opportunity. Every 16 pages bounds
      // the added latency (~a few hundred ms on a 1,000+ page file).
      if (i % 16 === 0) await new Promise((r) => setTimeout(r, 0));
    }
  } finally {
    await pdf.cleanup();
    // destroy() is safe and recommended to release the worker-side data.
    await loadingTask.destroy?.();
  }
  return pages;
}

/**
 * Private Use Area, U+E000–U+F8FF.
 *
 * pdf.js maps a symbolic font's raw bytes into U+F000–U+F0FF when the font has
 * no usable ToUnicode CMap, so decorative icons arrive as text items.
 * pdfplumber drops them, which is why this only bites the browser engine.
 *
 * On the real AppClose export those icons cost 43% of the corpus, silently:
 *
 *   "(See original message \uF0E5 on page 5)"   <- U+F0E5, not a space
 *   "\uF0C6 (See attached \uF03E on page 917)"  <- U+F0C6 LEADS the row
 *
 * Two distinct failures, which is why the fix is here and not in a regex.
 * The embedded icon defeats `\s+`, so the adapter's back-pointer and
 * attachment-ref patterns never match — quote blocks open and never close,
 * swallowing every record until the next date header. And the LEADING icon
 * owns the row's x0, moving the attachment ref from its own column (80.01)
 * onto the message-body column (71.02), so a ref is read as body text: a
 * token the sender never typed, written into evidence.
 *
 * Dropping them before rows are assembled fixes both — the ref regains its
 * column and the text closes up. These codepoints are never content: a PDF
 * text layer reaching the PUA means an unmapped or symbolic glyph.
 */
const PUA_ONLY = /[\uE000-\uF8FF]/g;

/** Cairo renders some emoji as a tiny run of their codepoint spelling ("01F",
 *  "92F", "923"), drawn at ~4.55 pt inside a 10 pt body line. Geometry is the
 *  only safe discriminator: "497" is indistinguishable from a typed number and
 *  many English words are hex-valid ("DAD", "BED", "FACE"), so a textual rule
 *  would delete real message text. Mirrors _ARTIFACT_MAX_HEIGHT in appclose.py. */
const ARTIFACT_MAX_HEIGHT = 6;
const HEX_TOKEN = /^[0-9A-F]{2,6}$/;

function isRenderArtifact(str: string, h: number): boolean {
  return h > 0 && h < ARTIFACT_MAX_HEIGHT && HEX_TOKEN.test(str.trim());
}

function stripPua(s: string): string {
  // No `test()` guard: `test` on a /g regex advances lastIndex, so repeated
  // calls alternate true/false and would strip every other row. `replace`
  // resets lastIndex itself, so calling it unconditionally is the safe form.
  return s.replace(PUA_ONLY, "");
}

/**
 * Group a page's text items into visual lines, keeping each line's leftmost x
 * and glyph height.
 *
 * ROW_TOL is 0.5 pt, matching evals/adapters/appclose.py: an emoji rendered
 * from a fallback font reports a baseline a fraction of a point off its
 * neighbours', and a stricter match strands it along with any body characters
 * fused into the same run. Wider merges adjacent lines, which appends text
 * after a record header's ":" so the header never completes.
 *
 * NOTE: the x values pdf.js reports are NOT always those pdfplumber reports.
 * pdfplumber measures the glyph's inked bounding box; pdf.js reports the
 * text-space pen position, and they differ by the font's left side bearing —
 * 0.48 pt on the real AppClose corpus, enough to move record headers onto the
 * section-header column. Column constants are therefore per-engine; see
 * APPCLOSE_COLUMNS in appclose.ts.
 *
 * Symbolic-font glyphs are dropped before rows are assembled — see PUA_ONLY.
 */
export function extractPageLines(textContent: { items: unknown[] }): PositionedLine[] {
  const positioned = textContent.items
    .map((raw) => raw as TextItemLike)
    .filter(
      (it) =>
        typeof it.str === "string" &&
        Array.isArray(it.transform) &&
        (it.transform as number[]).length >= 6,
    )
    .map((it) => ({
      str: stripPua(it.str as string),
      x: (it.transform as number[])[4],
      y: (it.transform as number[])[5],
      h: typeof it.height === "number" ? it.height : 0,
    }))
    // Blank items are KEPT: pdf.js emits the document's spaces as their own
    // items, and they are what separates words when the runs are concatenated.
    .filter((it) => it.str !== "");
  if (positioned.length === 0) return [];

  // y grows upward in PDF space, so descending y is top→bottom.
  positioned.sort((a, b) => b.y - a.y || a.x - b.x);

  const ROW_TOL = 0.5;
  const out: PositionedLine[] = [];
  let row: typeof positioned = [];
  let rowY: number | null = null;
  const flush = () => {
    if (!row.length) return;
    row.sort((a, b) => a.x - b.x);
    // Concatenate with NO separator, exactly as extractPageText does. Joining
    // with " " inserts spacing the document does not contain: pdf.js splits a
    // run at every style change, so a colon arrives as its own item and
    // "https://x" came out "https : //x" and ":)" came out ": )" -- message
    // text altered, in a corpus whose whole purpose is to be quoted verbatim.
    // Artifacts are dropped from the TEXT but still counted when the row's
    // column is read below: a sub-height run can be the leftmost item, and
    // removing it first re-seats the row on a column that isn't its own.
    const text = row
      .filter((r) => !isRenderArtifact(r.str, r.h))
      .map((r) => r.str)
      .join("")
      .replace(/\s+/g, " ")
      .trim();
    // Column identity comes from the leftmost item with actual ink: a leading
    // space (or a symbolic icon before it was stripped) must not claim the row.
    const lead = row.find((r) => r.str.trim() !== "") ?? row[0];
    if (text !== "") out.push({ x0: lead.x, height: lead.h, text });
    row = [];
  };
  for (const it of positioned) {
    if (rowY === null || Math.abs(it.y - rowY) <= ROW_TOL) {
      if (rowY === null) rowY = it.y;
      row.push(it);
    } else {
      flush();
      row = [it];
      rowY = it.y;
    }
  }
  flush();
  return out;
}

interface TextItemLike {
  str?: string;
  hasEOL?: boolean;
  // pdf.js transform: [a, b, c, d, e, f]; e = x, f = y (text-space baseline).
  transform?: number[];
  height?: number;
  // Rendered run width (device units) — used to measure intra-row gaps for
  // the column split below. Optional: pd.js always supplies it, but a row
  // with any width missing falls back to no-split (conservative).
  width?: number;
}

// Reconstruct one page's text in VISUAL reading order (top→bottom, left→right).
//
// pdf.js returns text items in content-stream order, which is NOT always the
// visual order: some OFW exports (observed on a real cover-less export) emit the
// per-message `Message N of M` header at the END of a page's stream, so the old
// concatenate-by-stream-order + hasEOL approach put that marker on the LAST line
// of the page. Splitting stanzas on a trailing marker misaligns every message
// (parser threw "message 2: missing Sent:"). pdfplumber reads strictly
// top-to-bottom, so the two engines diverged. Bucket items into rows by their
// baseline y, order rows top→bottom and items left→right within a row, so cloud
// extraction matches pdfplumber's reading order. Falls back to the old
// stream-order + hasEOL concat when items carry no position (rare).
// Exported for unit testing (the reading-order reconstruction); not part of the
// module's normal surface — callers use extractPdfTextEol.
export function extractPageText(textContent: { items: unknown[] }): string {
  const strItems = textContent.items
    .map((raw) => raw as TextItemLike)
    .filter((it) => typeof it.str === "string");
  const positioned = strItems
    .filter(
      (it) => Array.isArray(it.transform) && (it.transform as number[]).length >= 6,
    )
    .map((it) => ({
      str: it.str as string,
      x: (it.transform as number[])[4],
      y: (it.transform as number[])[5],
      h: typeof it.height === "number" ? it.height : 0,
      w: typeof it.width === "number" ? it.width : null,
    }));

  // Fallback to the original stream-order + hasEOL concat when ANY text item
  // lacks a usable position — not just when ALL do. Reordering by position only
  // works if every str item is placeable; a partially-positioned page would
  // otherwise silently DROP the unpositioned text. (pd.js text items normally
  // all carry a transform, so this is the rare/defensive path; byte-equal with
  // what shipped before for those PDFs.)
  if (positioned.length !== strItems.length) {
    const out: string[] = [];
    for (const raw of textContent.items) {
      const it = raw as TextItemLike;
      if (typeof it.str === "string") out.push(it.str);
      if (it.hasEOL) out.push("\n");
    }
    return out
      .join("")
      .split("\n")
      .map((line) => line.replace(/\s+$/g, ""))
      .join("\n")
      .replace(/\n+$/, "");
  }

  // Row-grouping tolerance: half a representative glyph height. Lines in OFW/CC
  // exports are well-separated, so this cleanly splits rows without merging.
  const heights = positioned
    .map((p) => p.h)
    .filter((h) => h > 0)
    .sort((a, b) => a - b);
  const medianH = heights.length ? heights[Math.floor(heights.length / 2)] : 12;
  const tol = Math.max(1, medianH * 0.5);

  // Order top→bottom (y descending: PDF y grows upward).
  positioned.sort((a, b) => b.y - a.y);

  // Column split: two-column layouts (the OFW cover's right-hand contact
  // block sits 5pt off the Timezone baseline — INSIDE the row tolerance) must
  // not concatenate onto one line ("Timezone:America/Chicagoourfamilywizard
  // .com" threw MalformedOFWError — live 2026-06-12). Within a row, a gap far
  // wider than word spacing starts a NEW line, so every character survives
  // (forensic rule: never silently drop) without corrupting the labeled line.
  // 3 lines-heights ≈ 3em — word gaps are well under 1em; true column gutters
  // are tens of points. Rows with any unmeasurable width never split.
  const colGap = Math.max(24, medianH * 3);

  // Group consecutive (in y order) items into rows within `tol` of the row's
  // top y, then emit each row left→right.
  const lines: string[] = [];
  let row: { x: number; str: string; w: number | null }[] = [];
  let rowY = positioned[0].y;
  const flush = () => {
    row.sort((a, b) => a.x - b.x);
    const widths = row.every((r) => r.w !== null);
    let line = row[0].str;
    for (let k = 1; k < row.length; k++) {
      const prev = row[k - 1];
      const gap = widths ? row[k].x - (prev.x + (prev.w as number)) : 0;
      if (gap > colGap) {
        lines.push(line);
        line = row[k].str;
      } else {
        line += row[k].str;
      }
    }
    lines.push(line);
    row = [];
  };
  for (const it of positioned) {
    if (rowY - it.y > tol) {
      flush();
      rowY = it.y;
    }
    row.push({ x: it.x, str: it.str, w: it.w });
  }
  if (row.length) flush();

  return lines
    .map((line) => line.replace(/\s+$/g, ""))
    .join("\n")
    .replace(/\n+$/, "");
}
